import { useT } from "@/lib/i18n-dict";
import { localeMeta } from "@/lib/seo-head";
import { useTx } from "@/lib/i18n-dict";
import { createFileRoute } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Section, SectionHeading } from "@/components/site/Section";
import { GhostCta, PrimaryCta } from "@/components/site/Cta";
import { AFFILIATE as AFFILIATE_SOURCE } from "@/content/pages";
import { alternateLinks } from "@/lib/i18n";

const TITLE = "Partnership Program — resell or implement factory.online";
const DESCRIPTION =
  "Join factory.online as a reseller or implementation partner: get API access, training, marketing support and a share of client success.";

export const Route = createFileRoute("/{-$locale}/affiliate-program")({
  staticData: { sitemap: true },
  head: ({ params }) => {
    const __m = localeMeta(params, TITLE, DESCRIPTION);
    return ({
    meta: [
      { title: __m.title },
      { name: "description", content: __m.description },
      { property: "og:title", content: __m.title },
      { property: "og:description", content: __m.description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: alternateLinks("/affiliate-program"),
  });
  },
  component: Affiliate,
});

function Affiliate() {
  const AFFILIATE = useTx(AFFILIATE_SOURCE);
  const t = useT();
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96"
          style={{ background: "var(--gradient-edge)" }}
        />
        <div className="container-page relative py-20 md:py-28">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
              {AFFILIATE.intro.eyebrow}
            </p>
            <h1 className="mt-3 font-display text-4xl leading-[1.05] font-bold text-balance-tight sm:text-5xl">
              {AFFILIATE.intro.title}
            </h1>
            <p className="mt-6 text-base text-muted-foreground md:text-lg">
              {AFFILIATE.intro.lead}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <PrimaryCta to="/registration-partners">{AFFILIATE.apply.cta}</PrimaryCta>
              <GhostCta to="/contacts">{t("Talk to us")}</GhostCta>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Formats */}
      <Section>
        <SectionHeading
          eyebrow="Two partnership formats"
          title="Choose the collaboration format that matches your expertise"
          lead="From sales to implementation — together we create more opportunities for businesses."
        />
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          {AFFILIATE.formats.map((format, index) => (
            <Reveal
              key={format.name}
              delay={index * 60}
              className="rounded-xl border border-border bg-surface/40 p-7"
            >
              <h3 className="font-display text-xl font-semibold">{format.name}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{format.description}</p>
              <ul className="mt-6 space-y-3">
                {format.benefits.map((benefit) => (
                  <li key={benefit} className="flex gap-2.5 text-sm text-muted-foreground">
                    <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                    {benefit}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Client benefits */}
      <Section className="bg-surface/40">
        <SectionHeading eyebrow="For clients" title="What the partnership model gives clients" />
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {AFFILIATE.clientBenefits.map((item, index) => (
            <Reveal
              key={item.title}
              delay={index * 50}
              className="rounded-xl border border-border p-6"
            >
              <h3 className="font-display text-base font-semibold">{item.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{item.text}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* How it works */}
      <Section>
        <SectionHeading eyebrow="How to become a partner" title={AFFILIATE.apply.title} lead={AFFILIATE.apply.lead} />
        <ol className="mt-10 grid gap-6 md:grid-cols-3">
          {AFFILIATE.howItWorks.map((step, index) => (
            <Reveal
              key={step}
              delay={index * 60}
              className="rounded-xl border border-border bg-surface/40 p-6"
            >
              <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
                Step 0{index + 1}
              </p>
              <p className="mt-3 text-sm text-muted-foreground">{step}</p>
            </Reveal>
          ))}
        </ol>
      </Section>

      {/* Closing CTA */}
      <Section className="bg-surface/40">
        <Reveal className="rounded-2xl border border-primary/30 bg-surface p-10 text-center shadow-[var(--glow-primary)] md:p-16">
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-balance-tight md:text-4xl">
            {t("Ready to bring factory.online to your clients?")}
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryCta to="/registration-partners">{AFFILIATE.apply.cta}</PrimaryCta>
            <GhostCta to="/contacts">{t("Talk to us")}</GhostCta>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
